/*
The one place that decides which backend the app talks to.

Every request used to compute its own base from the build-time env vars:

    const BASE_ROUTE = process.env.REACT_APP_BACK_END_SERVER + ':' + process.env.REACT_APP_BACK_END_PORT

CRA inlines those at build time, so the API host is welded into the bundle. That
is fine for prod and fatal for testing: pointing the site at the Zuplo dev
gateway meant rebuilding the whole app, and the CI e2e job — which builds against
http://127.0.0.1:9, the discard port — can never exercise a real API path at all.
A full-site regression through the gateway (guardrail 5, P4) needs one build that
can be aimed at several origins.

So the base is now resolved at REQUEST time, from the first of:

  1. `window.__OPTIMICITI_API_BASE__` — set by public/env.js, which the browser
     loads before the bundle. Empty/absent means "not overridden".
  2. The build-time env vars, joined exactly as before.

Prod is byte-identical: public/env.js ships with the override commented out, so
branch 1 is undefined and branch 2 produces the same string the old constant did.
Nothing about a normal page load changes.

Resolved per call rather than memoised. A Playwright `addInitScript` sets the
global before any module evaluates, so memoising would work too — but a value
that can be read once and then silently ignored is exactly the kind of seam that
tests pass against while the app does something else, and this costs a string
concat on a call that is about to cross the network.
*/

/**
 * The runtime override, if the page set one.
 *
 * Guarded for non-browser contexts (Jest's node environment, any SSR/prerender
 * pass) where `window` doesn't exist. Never throws: a broken override must fall
 * back to the built-in base, not take the app down.
 *
 * @returns {string|null}
 */
function runtimeOverride() {
  try {
    if (typeof window === 'undefined') return null;
    const value = window.__OPTIMICITI_API_BASE__;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Base URL for every backend request, with no trailing slash.
 *
 * Callers append their own separator, as they always have — the two shapes in
 * the tree are `apiBase() + '/' + route` (route stripped of its leading slash)
 * and `apiBase() + route` (route keeps it). Stripping a trailing slash here
 * keeps both correct when an override is pasted in with one, which is how URLs
 * arrive when a person copies them out of a dashboard.
 *
 * @returns {string}
 */
export function apiBase() {
  const override = runtimeOverride();
  const base = override
    || (process.env.REACT_APP_BACK_END_SERVER + ':' + process.env.REACT_APP_BACK_END_PORT);
  return base.replace(/\/+$/, '');
}

export default apiBase;
